import {
  Directive,
  OnDestroy,
  OnInit,
  HostListener,
  Injector,
  inject,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { UserPreferencesService } from '@alfresco/adf-core';
import { SetSelectedNodesAction } from '@alfresco/aca-shared/store';
import { NodeEntry } from '@alfresco/js-api';

import { WkDocumentListComponent } from '../components/wk-document-list/wk-document-list.component';
import { DocumentListService } from '@alfresco/adf-content-services';

@Directive({
  selector: '[acaWkDocumentList]',
  standalone: false,
})
export class WkDocumentListDirective implements OnInit, OnDestroy {
  private isLibrary = false;
  selectedNode: NodeEntry;

  onDestroy$ = new Subject<boolean>();

  get sortingPreferenceKey(): string {
    return this.route.snapshot.data.sortingPreferenceKey;
  }

  private readonly preferences: UserPreferencesService;
  private readonly store: Store<any>;
  private readonly router: Router;
  private readonly route: ActivatedRoute;
  private readonly documentList: WkDocumentListComponent;
  private readonly documentListService = inject(DocumentListService);

  constructor(injector: Injector) {
    this.store = injector.get<Store<any>>(Store);
    this.router = injector.get<Router>(Router);
    this.route = injector.get<ActivatedRoute>(ActivatedRoute);
    this.preferences = injector.get<UserPreferencesService>(
      UserPreferencesService
    );
    this.documentList = injector.get<WkDocumentListComponent>(
      WkDocumentListComponent
    );
  }

  ngOnInit() {
    this.documentList.stickyHeader = true;
    this.documentList.includeFields = [
      'isFavorite',
      'aspectNames',
      'definition',
    ];
    this.isLibrary =
      this.documentList.currentFolderId === '-mysites-' ||
      // workaround for custom node list
      this.router.url.endsWith('/libraries') ||
      this.router.url.startsWith('/search-libraries');

    if (this.sortingPreferenceKey) {
      const current = this.documentList.sorting;

      const key = this.preferences.get(
        `${this.sortingPreferenceKey}.sorting.key`,
        current[0]
      );
      const direction = this.preferences.get(
        `${this.sortingPreferenceKey}.sorting.direction`,
        current[1]
      );

      this.documentList.sorting = [key, direction];
      // TODO: bug in ADF, the `sorting` binding is not updated when changed from code
      this.documentList.data.setSorting({ key, direction });
    }

    this.documentList.ready
      .pipe(
        filter(() => !this.router.url.includes('viewer:view')),
        takeUntil(this.onDestroy$)
      )
      .subscribe(() => this.onReady());

    this.documentListService.reload$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.reload(this.selectedNode);
      });

    this.documentListService.resetSelection$
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.reset();
      });
  }

  ngOnDestroy() {
    this.onDestroy$.next(true);
    this.onDestroy$.complete();
  }

  @HostListener('sorting-changed', ['$event'])
  onSortingChanged(event: CustomEvent) {
    if (this.sortingPreferenceKey) {
      this.preferences.set(
        `${this.sortingPreferenceKey}.sorting.key`,
        event.detail.sortingKey || event.detail.key
      );
      this.preferences.set(
        `${this.sortingPreferenceKey}.sorting.direction`,
        event.detail.direction
      );
    }
  }

  @HostListener('node-select', ['$event'])
  onNodeSelect(event: CustomEvent) {
    if (!!event.detail && !!event.detail.node) {
      this.updateSelection();
      this.selectedNode = event.detail.node;
    }
  }

  @HostListener('node-unselect')
  onNodeUnselect() {
    this.updateSelection();
  }

  onReady() {
    this.updateSelection();
  }

  private updateSelection() {
    const selection = this.documentList.selection.map((node) => {
      node['isLibrary'] = this.isLibrary;
      return node;
    });

    this.store.dispatch(new SetSelectedNodesAction(selection));
  }

  private reload(selectedNode?: NodeEntry) {
    this.documentList.resetSelection();
    if (selectedNode) {
      this.store.dispatch(new SetSelectedNodesAction([selectedNode]));
    } else {
      this.store.dispatch(new SetSelectedNodesAction([]));
    }
    this.documentList.reload();
  }

  private reset() {
    this.documentList.resetSelection();
    this.store.dispatch(new SetSelectedNodesAction([]));
  }
}
